/**
 * ViewerSettingsPanel.js
 * A reusable, portal-based modal for controlling viewer settings (Theme, Font Size).
 * Decoupled from the main viewer to ensure clean architecture.
 */
import { ViewerSettingsService } from "../../../services/viewer-settings.service.js";

export class ViewerSettingsPanel {
    constructor(options = {}) {
        this.uid = options.uid || null;
        this.onSettingsChange = options.onSettingsChange || (() => { });
        this.onClose = options.onClose || (() => { });

        // Initial State
        this.settings = {
            theme: 'theme-dark',
            fontSize: 18
        };

        this.panelId = `settings-panel-${Date.now()}`;
        this.backdropId = `settings-backdrop-${Date.now()}`;
        this.element = null;
        this.backdrop = null;

        this.init();
    }

    async init() {
        // Load initial settings
        const saved = await ViewerSettingsService.loadSettings(this.uid);
        if (saved) {
            this.settings = { ...this.settings, ...saved };
        }

        this.render();
        this.bindEvents();

        // Notify initial application
        this.onSettingsChange({
            theme: this.settings.theme,
            fontSize: this.settings.fontSize
        });
    }

    render() {
        const { fontSize, theme } = this.settings;

        const html = `
            <div id="${this.backdropId}" class="fixed inset-0 bg-black/50 hidden z-[9998] backdrop-blur-sm transition-opacity duration-300 opacity-0"></div>
            <div id="${this.panelId}" class="fixed bottom-0 left-0 right-0 w-full max-w-lg mx-auto bg-zinc-900 border-t border-zinc-800 p-6 pb-24 rounded-t-2xl shadow-2xl transform translate-y-full transition-transform duration-300 z-[9999]">
                <div class="flex justify-between items-center mb-6 text-white">
                    <span class="font-bold text-lg">뷰어 설정</span>
                    <button class="close-settings p-2 -mr-2 text-zinc-400 hover:text-white rounded-full hover:bg-zinc-800 transition-colors">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>
                
                <div class="space-y-8">
                    <!-- Font Size -->
                    <div>
                        <div class="flex justify-between items-end mb-3">
                            <span class="text-xs text-zinc-500 font-bold uppercase tracking-wider">글자 크기</span>
                            <span class="text-xs text-indigo-400 font-bold" id="font-size-display">${fontSize}px</span>
                        </div>
                        <div class="relative h-10 flex items-center">
                            <input type="range" min="14" max="28" step="1" value="${fontSize}" 
                                class="font-size-range w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50">
                        </div>
                        <div class="flex justify-between text-[10px] text-zinc-600 font-bold px-1 mt-1">
                            <span>가</span>
                            <span class="text-base">가</span>
                        </div>
                    </div>

                    <!-- Theme -->
                    <div>
                        <div class="text-xs text-zinc-500 font-bold uppercase mb-3 tracking-wider">테마 설정</div>
                        <div class="grid grid-cols-4 gap-3">
                            ${this.renderThemeBtn('theme-white', 'bg-white border-zinc-200', 'White')}
                            ${this.renderThemeBtn('theme-sepia', 'bg-[#f5f0e1] border-[#e8e2cd]', 'Sepia')}
                            ${this.renderThemeBtn('theme-dark', 'bg-[#18181b] border-zinc-700', 'Dark')}
                            ${this.renderThemeBtn('theme-black', 'bg-black border-zinc-800', 'Black')}
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Create container and append to body
        const container = document.createElement('div');
        container.innerHTML = html;
        document.body.appendChild(container); // Portal to body

        this.element = document.getElementById(this.panelId);
        this.backdrop = document.getElementById(this.backdropId);
        this.container = container;

        // Set initial active theme state
        this.updateActiveThemeUI(theme);
    }

    renderThemeBtn(theme, classes, label) {
        return `
            <button class="theme-btn group relative w-full aspect-square rounded-2xl border-2 ${classes} shadow-sm transition-all duration-200 hover:scale-105 focus:outline-none" data-theme="${theme}" aria-label="${label}">
                <div class="absolute inset-0 rounded-xl ring-2 ring-transparent transition-all duration-200 group-hover:ring-indigo-500/30"></div>
                ${theme === 'theme-black' ? '<span class="text-[10px] text-zinc-500 font-bold">OLED</span>' : ''}
                <div class="active-indicator absolute top-2 right-2 w-2 h-2 bg-indigo-500 rounded-full opacity-0 transform scale-0 transition-all duration-300"></div>
            </button>
        `;
    }

    bindEvents() {
        const { element, backdrop } = this;

        // Close Actions
        element.querySelector('.close-settings').onclick = () => this.hide();
        backdrop.onclick = () => this.hide();

        // Font Size
        const range = element.querySelector('.font-size-range');
        const display = element.querySelector('#font-size-display');
        range.oninput = (e) => {
            const val = parseInt(e.target.value);
            display.textContent = `${val}px`;
            this.updateSettings({ fontSize: val });
        };

        // Theme
        const themeBtns = element.querySelectorAll('.theme-btn');
        themeBtns.forEach(btn => {
            btn.onclick = () => {
                const newTheme = btn.dataset.theme;
                this.updateActiveThemeUI(newTheme);
                this.updateSettings({ theme: newTheme });
            };
        });
    }

    updateActiveThemeUI(activeTheme) {
        const btns = this.element.querySelectorAll('.theme-btn');
        btns.forEach(btn => {
            const indicator = btn.querySelector('.active-indicator');
            if (btn.dataset.theme === activeTheme) {
                btn.classList.add('ring-2', 'ring-indigo-500', 'ring-offset-2', 'ring-offset-zinc-900', 'scale-95');
                if (indicator) {
                    indicator.classList.remove('opacity-0', 'scale-0');
                }
            } else {
                btn.classList.remove('ring-2', 'ring-indigo-500', 'ring-offset-2', 'ring-offset-zinc-900', 'scale-95');
                if (indicator) {
                    indicator.classList.add('opacity-0', 'scale-0');
                }
            }
        });
    }

    updateSettings(newPartial) {
        this.settings = { ...this.settings, ...newPartial };

        // Notify Parent/Callback with the updated fields (or full state if easier, but let's pass partials for efficiency or checking)
        // Actually, passing the specific change is clearer, but for consistency let's pass a struct.
        // The parent checks if properties exist.

        this.onSettingsChange(newPartial);

        // 2. Persist
        ViewerSettingsService.saveSettings(this.uid, newPartial);
    }

    toggle() {
        if (this.isVisible) this.hide();
        else this.show();
    }

    show() {
        this.isVisible = true;
        this.backdrop.classList.remove('hidden');
        // Force reflow for transition
        void this.backdrop.offsetWidth;

        this.backdrop.classList.remove('opacity-0');
        this.element.classList.remove('translate-y-full');
    }

    hide() {
        this.isVisible = false;
        this.element.classList.add('translate-y-full');
        this.backdrop.classList.add('opacity-0');

        setTimeout(() => {
            if (!this.isVisible) {
                this.backdrop.classList.add('hidden');
                this.onClose();
            }
        }, 300);
    }

    destroy() {
        if (this.container) {
            this.container.remove();
        }
    }
}
